type FetchFunction = () => Promise<any>;

interface Subscription {
    id: string;
    topic: string;
    callback: (data: any) => void;
}

class PollingService {
    private readonly subscriptions: Map<string, Subscription> = new Map();
    private readonly intervals: Map<string, NodeJS.Timeout> = new Map();
    private readonly subscriberCount: Map<string, number> = new Map();
    private readonly fetchFunctions: Map<string, FetchFunction> = new Map();

    /**
     * Subscribe to a topic with a polling interval.
     * @param topic Unique key for the polling resource (e.g., "incidents", "chat_123")
     * @param fetchFn Function that returns a Promise with the data
     * @param callback Function called when data is successfully fetched
     * @param interval Polling interval in milliseconds (default: 5000ms)
     * @returns subscriptionId
     */
    subscribe(
        topic: string,
        fetchFn: FetchFunction,
        callback: (data: any) => void,
        interval: number = 5000
    ): string {
        const id = Math.random().toString(36).substring(2, 11);
        
        this.subscriptions.set(id, { id, topic, callback });
        
        // Update subscriber count
        const currentCount = this.subscriberCount.get(topic) || 0;
        this.subscriberCount.set(topic, currentCount + 1);
        
        // Update fetch function (always use the latest one provided)
        this.fetchFunctions.set(topic, fetchFn);

        // Start polling if not already started
        if (!this.intervals.has(topic)) {
            // Initial fetch immediately
            this.poll(topic);
            
            // Set up interval
            const intervalId = setInterval(() => {
                this.poll(topic);
            }, interval);
            
            this.intervals.set(topic, intervalId);
            console.log(`[PollingService] Started polling for topic: ${topic}`);
        }

        return id;
    }

    unsubscribe(id: string) {
        const subscription = this.subscriptions.get(id);
        if (!subscription) return;

        const { topic } = subscription;
        this.subscriptions.delete(id);

        const currentCount = this.subscriberCount.get(topic) || 0;
        const newCount = Math.max(0, currentCount - 1);
        this.subscriberCount.set(topic, newCount);

        // If no more subscribers, stop polling
        if (newCount === 0) {
            this.stopPolling(topic);
        }
    }

    private stopPolling(topic: string) {
        const intervalId = this.intervals.get(topic);
        if (intervalId) {
            clearInterval(intervalId);
            this.intervals.delete(topic);
            console.log(`[PollingService] Stopped polling for topic: ${topic}`);
        }
        this.fetchFunctions.delete(topic);
    }

    private async poll(topic: string) {
        const fetchFn = this.fetchFunctions.get(topic);
        if (!fetchFn) return;

        try {
            const data = await fetchFn();
            
            // Broadcast to all subscribers of this topic
            this.subscriptions.forEach((sub) => {
                if (sub.topic === topic) {
                    sub.callback(data);
                }
            });
        } catch (error) {
            console.error(`[PollingService] Error polling topic ${topic}:`, error);
        }
    }

    /**
     * Clear all subscriptions and stop all polling.
     * Primarily for testing.
     */
    reset() {
        this.intervals.forEach((intervalId) => clearInterval(intervalId));
        this.intervals.clear();
        this.subscriptions.clear();
        this.subscriberCount.clear();
        this.fetchFunctions.clear();
    }
}

export const pollingService = new PollingService();
